import { memo, useMemo } from "react";
import { Div, Icon, useTheme, type DivProps } from "react-better-html";

type StarType = "star4" | "star4Outline";

type Star = {
   id: number;
   left: number;
   size: number;
   rotation: number;
   duration: number;
   delay: number;
   spinsClockwise: boolean;
   type: StarType;
};

function randomBetween(min: number, max: number) {
   return min + Math.random() * (max - min);
}

type StarsAnimationDecorationProps = DivProps & {
   count?: number;
   color?: string;
   minSize?: number;
   maxSize?: number;
   minDuration?: number;
   maxDuration?: number;
   opacity?: React.CSSProperties["opacity"];
};

function StarsAnimationDecoration({
   count = 32,
   color,
   minSize = 10,
   maxSize = 32,
   minDuration = 16,
   maxDuration = 30,
   ...props
}: StarsAnimationDecorationProps) {
   const theme = useTheme();

   const stars = useMemo<Star[]>(
      () =>
         Array.from({ length: count }, (_, index) => {
            const duration = randomBetween(minDuration, maxDuration);

            return {
               id: index,
               left: randomBetween(0, 100),
               size: randomBetween(minSize, maxSize),
               rotation: randomBetween(0, 360),
               duration,
               delay: -randomBetween(0, duration),
               spinsClockwise: randomBetween(0, 1) > 0.5,
               type: randomBetween(0, 1) > 0.5 ? "star4" : "star4Outline",
            };
         }),
      [count, minSize, maxSize, minDuration, maxDuration],
   );

   const starColor = color ?? theme.colors.base;

   return (
      <Div width="100%" height="100%" overflow="hidden" pointerEvents="none" {...props}>
         {stars.map((star) => (
            <Div
               position="absolute"
               top={0}
               left={`${star.left}%`}
               width={star.size}
               height="100%"
               animation={`starsFloatAnimation ${star.duration}s linear ${star.delay}s infinite normal none running`}
               key={star.id}
            >
               <Div
                  width={star.size}
                  height={star.size}
                  animation={`starsShrinkAnimation ${star.duration}s linear ${star.delay}s infinite normal none running`}
               >
                  <Icon
                     name={star.type}
                     color={starColor}
                     size={star.size}
                     rotate={`${star.rotation}deg`}
                     animation={`starsSpinAnimation ${star.duration}s linear ${star.delay}s infinite ${
                        star.spinsClockwise ? "normal" : "reverse"
                     } none running`}
                  />
               </Div>
            </Div>
         ))}
      </Div>
   );
}

export default memo(StarsAnimationDecoration);
